// src/lib/socket.js
import { io } from "socket.io-client";
import { getStoredToken } from "./authStorage";

// --- DEBUG: base + token peek ---
function _peekToken(tok) {
  if (!tok) return 'none';
  return `len:${String(tok).length} head:${tok.slice(0, 10)}`;
}
const bootOrigin =
  (typeof window !== "undefined" && window.location)
    ? `${window.location.protocol}//${window.location.hostname}`
    : "node";

console.log(
  "[socket] boot apiBase=",
  bootOrigin,
  "env=",
  process.env.REACT_APP_API_BASE_URL || process.env.VITE_API_BASE_URL || "(same-host)"
);

// 🔒 Try common keys; keep "token" as primary for your app
function getToken() {
  try {
    const direct = getStoredToken();
    if (direct) return direct;

    const fallback = localStorage.getItem("auth_token");
    if (fallback) return fallback;

    const rawUser = localStorage.getItem("auth_user");
    if (rawUser) {
      try {
        const u = JSON.parse(rawUser);
        return u?.token || u?.jwt || null;
      } catch { /* ignore */ }
    }
    return null;
  } catch {
    return null;
  }
}

// 🌐 Base URL: env overrides, else same host as page (works on LAN)
function apiBase() {
  const env =
    process.env.REACT_APP_API_BASE_URL ||
    process.env.VITE_API_BASE_URL;

  // ⚠️ For HTTP API, "/api" is fine.
  // ⚠️ For Socket.IO, "/api" is WRONG and causes "Invalid namespace".
  // So only use env if it is a full absolute URL.
  if (env && /^https?:\/\//i.test(env)) {
    return env;
  }

  // Use same origin so nginx can proxy /socket.io/
  return window.location.origin;
}

function socketOptions(token) {
  return {
    transports: ["websocket", "polling"],
    withCredentials: true,
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    timeout: 20000,
    path: "/socket.io",
    auth: { token },
  };
}

let socket;

export function getSocket() {
  if (!socket) {
    socket = io(apiBase(), socketOptions(getToken()));
console.log("[socket] io init opts", {
  transports: (socket?.io?.opts?.transports || "n/a"),
  withCredentials: (socket?.io?.opts?.withCredentials || false),
  hasExtraHeaders: !!(socket?.io?.opts?.extraHeaders),
  authPeek: _peekToken((socket && socket.auth && socket.auth.token) || null),
});

// lifecycle listeners
socket.on("connect", () => {
  console.log("[socket] CONNECTED id=", socket.id);
});
socket.on("disconnect", (reason) => {
  console.warn("[socket] DISCONNECT reason=", reason);
});
socket.on("connect_error", (err) => {
  console.error("[socket] CONNECT_ERROR", { message: err?.message, stack: err?.stack });
});
socket.io.on("reconnect_attempt", (n) => {
  console.log("[socket] RECONNECT_ATTEMPT #", n);
});
socket.io.on("reconnect", (n) => {
  console.log("[socket] RECONNECTED after attempts=", n);
});
socket.io.on("error", (e) => {
  console.error("[socket] IO ERROR", e);
});

// optional: wrap emit to log all emits (do this once)
if (!socket.__emitWrapped) {
  const _origEmit = socket.emit.bind(socket);
  socket.emit = (ev, payload) => {
    const brief =
      payload && typeof payload === "object"
        ? Object.fromEntries(Object.entries(payload).slice(0, 5))
        : payload;
    console.log("[socket] EMIT", ev, brief);
    return _origEmit(ev, payload);
  };
  socket.__emitWrapped = true;
}

    // Optional: basic visibility into auth failures
    socket.on("connect_error", (err) => {
      if (String(err?.message || "").toLowerCase().includes("unauthorized")) {
        console.warn("[socket] Unauthorized during connect. Check JWT/token.");
      }
    });
  }
  return socket;
}

// ✅ Call this after login/logout to refresh the JWT used by the existing socket
export function refreshSocketAuth(nextToken = getToken()) {
  const s = getSocket();
  console.log("[socket] refreshSocketAuth called, new token", _peekToken(nextToken));
  s.auth = { token: nextToken };
  // keep header in sync too
  if (!s.io.opts) s.io.opts = {};
  // If already connected, do a quick reconnect so the new auth takes effect
  if (s.connected) {
    try { s.disconnect(); } catch {}
  }
  s.connect();
}

// (Optional) auto-refresh if another tab updates the token
window.addEventListener("storage", (e) => {
  console.log("[socket] storage event triggered, key=", e.key);
  if (e.key === "token" || e.key === "auth_token" || e.key === "auth_user") {
    console.log("[socket] token change detected, refreshing socket auth");
    refreshSocketAuth();
  }
});